import { useCallback, useState } from "react";
import { importUsers as importUsersApi, UserImportServiceError } from "../api/users.api";
import type { UserImportItem, UserImportResponse, UserImportValidationError } from "../types/user.types";

export interface UseImportUsersReturn {
  importUsers: (users: UserImportItem[]) => Promise<UserImportResponse>;
  loading: boolean;
  error: string | null;
  importErrors: UserImportValidationError[];
  reset: () => void;
}

export function useImportUsers(): UseImportUsersReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<UserImportValidationError[]>([]);

  const reset = useCallback(() => {
    setError(null);
    setImportErrors([]);
  }, []);

  const importUsers = useCallback(async (users: UserImportItem[]) => {
    setLoading(true);
    reset();
    try {
      return await importUsersApi(users);
    } catch (requestError: unknown) {
      const message = requestError instanceof Error ? requestError.message : "Failed to import users";
      setError(message);
      if (requestError instanceof UserImportServiceError) setImportErrors(requestError.importErrors);
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, [reset]);

  return { importUsers, loading, error, importErrors, reset };
}
